import * as BusinessManagerApi from '@wix/business-manager-api';
import * as classnames from 'classnames';
import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import ChevronDown from 'wix-ui-icons-common/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/ChevronUp';
import ExternalLink from 'wix-ui-icons-common/ExternalLink';
import { TextButton, Heading, IconButton, Text, ToggleSwitch } from 'wix-style-react';
import { ChannelFilter } from '../../../../consts/channel-filter';
import * as DataHooks from '../../../../consts/data-hooks';
import { InjectedBiLoggerProps, provideLogger } from '../../../../services/bi-logger/logger-provider';
import { SETTINGS_CHANNEL, SETTINGS_GROUP_ROW, SETTINGS_GROUP_TOGGLE_SWITCH } from '../../../../utils/data-hook-utils';
import { HeightAnimationContainer } from '../../height.animation.container';
import { Section } from '../types';
import * as style from './style.scss';

const EMAIL_SUBSCRIPTIONS_URL = 'https://www.wix.com/email-subscriptions-preferences';

export interface ChannelSectionProps {
  channelKey: ChannelFilter;
  channelTitle: string;
  channelDescription: string;
  subsections: Section[];
  onGroupSettingChanged: (subsectionIndx: number, groupIndx: number) => void;
  initiallyOpen?: boolean;
  restaurantsAppState: string;
}

class ChannelSectionComponent extends React.Component<ChannelSectionProps & InjectedBiLoggerProps & InjectedTranslateProps, { isChannelSectionOpen: boolean }> {
  constructor(props: ChannelSectionProps & InjectedBiLoggerProps & InjectedTranslateProps) {
    super(props);
    this.state = { isChannelSectionOpen: props.initiallyOpen || false };
  }

  private toggleOpenState() {
    const { logger, channelKey } = this.props;
    const { isChannelSectionOpen } = this.state;
    logger.reportCollapseExpandChannel(channelKey, !isChannelSectionOpen);
    this.setState({ isChannelSectionOpen: !isChannelSectionOpen });
  }

  private onEmailSubscriptionsLinkClick(linkHref: string) {
    const { logger, channelKey, subsections } = this.props;
    const url = linkHref || EMAIL_SUBSCRIPTIONS_URL;
    logger.reportClickOnChannelLink(channelKey, url, subsections.length > 0);
  }

  private navigateToRestaurants() {
    const { restaurantsAppState } = this.props;
    window['ModuleRegistry'].invoke('businessManager.navigateTo', BusinessManagerApi.appDefIds.restaurants, restaurantsAppState);
  }

  private renderChevron() {
    const { isChannelSectionOpen } = this.state;
    if (isChannelSectionOpen) {
      return <ChevronUp data-hook={DataHooks.CHEVRON_UP}/>;
    } else {
      return <ChevronDown data-hook={DataHooks.CHEVRON_DOWN}/>;
    }
  }

  private renderContent() {
    const { subsections, onGroupSettingChanged, channelTitle, restaurantsAppState } = this.props;
    const content = subsections.map((subsection, subsectionIndx) => {
      return [
        this.renderSubsection(subsection),
        ...this.renderNotificationGroups(subsection, channelTitle, onGroupSettingChanged, subsectionIndx),
        (subsectionIndx !== subsections.length - 1) ? <div key={`div-${subsection}`} className={style.divider}/> : null
      ];
    });
    if (restaurantsAppState) {
      content.push(this.renderRestaurantsGroup());
    }
    return content;
  }

  private renderSubsection(subsection) {
    return <div key={subsection} className={classnames(style['settings-group'], style.header)}>
      <Text size="small" weight="bold" data-hook={DataHooks.SETTING_SUBSECTION}>{subsection.sectionTitle}</Text>
    </div>;
  }

  private renderNotificationGroups(section: Section, channelTitle: string, onGroupSettingChanged: (subsectionIndx: number, groupIndx: number) => void, subsectionIndx) {
    return section.groups.map((group, groupIndx) => {
      return (
        <div
          key={`${channelTitle}:${section.sectionTitle}:${group.groupKey}`}
          className={classnames(style['settings-group'], style.line)}
          data-hook={SETTINGS_GROUP_ROW(group.groupKey)}
        >
          <Text size="small" weight="thin" data-hook={DataHooks.SETTING_GROUP}>{group.groupTitle}</Text>
          <ToggleSwitch dataHook={SETTINGS_GROUP_TOGGLE_SWITCH(section.sectionKey, group.groupKey)} checked={!group.isMuted} onChange={() => onGroupSettingChanged(subsectionIndx, groupIndx)}/>
        </div>
      );
    });
  }

  private renderRestaurantsGroup() {
    const { t } = this.props;
    return [
      <div key={`div-restaurants`} className={style.divider}/>,
      <div key={'subsection.restaurants.title'} className={classnames(style['settings-group'], style.header)}>
        <Text size="small" weight="bold" dataHook={DataHooks.SETTING_SUBSECTION}>{t('subsection.restaurants.title')}</Text>
      </div>,
      <div
        key={`subsection.restaurants.link`}
        className={classnames(style['settings-group'], style.line)}
        data-hook={SETTINGS_GROUP_ROW('restaurants')}
      >
        <TextButton as="button" size="small" weight="thin" data-hook={DataHooks.RESTAURANTS_LINK} onClick={() => this.navigateToRestaurants()}>{t('subsection.restaurants.link')}</TextButton>
      </div>
    ];
  }

  render() {
    const { channelTitle, channelDescription, channelKey, subsections, t } = this.props;
    const hasContent = subsections.length > 0;
    const isOpen = hasContent && this.state.isChannelSectionOpen;
    return (
      <section
        data-hook={SETTINGS_CHANNEL(channelKey)}
        className={classnames(style['settings-section'], style['settings-channel'], { [style.open]: isOpen })}>
        <span className={style.content}>
          <div data-hook={DataHooks.CHANNEL_HEADER} className={style['settings-channel-header']} onClick={() => hasContent && this.toggleOpenState()}>
            <div>
              <Heading appearance="H3">{channelTitle}</Heading>
              {
                hasContent &&
                <Text data-hook={DataHooks.CHANNEL_DESCRIPTION} size="medium" weight="thin" tagName="div" secondary>
                  {channelDescription}
                </Text>
              }
              {
                channelKey === ChannelFilter.EMAIL &&
                <Text size="medium" weight="thin" tagName="div" secondary>
                  {t('channel.email.secondLine')}
                  {' '}
                  <TextButton as="a"
                              href={EMAIL_SUBSCRIPTIONS_URL}
                              target="_blank"
                              suffixIcon={<ExternalLink/>}
                              onClick={() => this.onEmailSubscriptionsLinkClick(EMAIL_SUBSCRIPTIONS_URL)}>
                    {t('channel.email.preferencesLink')}
                  </TextButton>
                </Text>
              }
            </div>
            {
              hasContent &&
              <IconButton
                as="button"
                priority="secondary"
                size="small"
                className={style.chevron}
              >
                {this.renderChevron()}
              </IconButton>
            }
          </div>
          <div className={classnames(style['divider-full'], { [style.open]: isOpen })}/>
          <HeightAnimationContainer shouldDisplay={isOpen}>
            <div className={style['settings-channel-content']}>
              {this.renderContent()}
            </div>
          </HeightAnimationContainer>
        </span>
      </section>
    );
  }
}

export const ChannelSection = provideLogger<ChannelSectionProps>(translate()(ChannelSectionComponent));
